import axiosInterceptor from '../helpers/axiosInterceptor';
import {
  GET_CONTENT_ARTICLE,
  GET_CONTENT_RECOMMENDATIONS,
  SAVE_CONTENT_ENGAGEMENT,
} from '../Constants';
import { FIXTURE_MODULE, FIXTURE_RECOMMENDATIONS } from './trainingFixture';

/**
 * Training content service.
 *
 * Endpoints (CONTENT-MODEL-DESIGN.md section 5):
 *   GET  /content/articles/{slug}                  -> article + ordered sections
 *   GET  /content/recommendations?assessmentId={id} -> ranked items for a result
 *   POST /content/engagement                       -> record SHOWN / OPENED / COMPLETED / HELPFUL / NOT_HELPFUL
 *
 * A training module is a `content_article` with `content_type = 'MODULE'`
 * (section 8). Each phase is a `content_section` carrying a `section_type` -
 * one of UNDERSTAND, NOTICE, PRACTICE, PLAN, REHEARSE, CHECKIN - plus a
 * `config` object holding the data that phase's interaction needs (cue lists,
 * scenario choices, rehearsal schedule). `body` stays what it is elsewhere in
 * the model: sanitized rich text, rendered through helpers/safeHtml.
 *
 * Expected response, in the envelope every other service here returns
 * ({ status, data } under response.data):
 *
 *   { "status": true,
 *     "data": {
 *       "articleId": 12,
 *       "slug": "emotional-reactivity",
 *       "title": "Emotional Reactivity",
 *       "summary": "...",
 *       "contentType": "MODULE",
 *       "sections": [
 *         { "sectionId": 91, "slug": "notice", "sectionType": "NOTICE",
 *           "heading": "...", "displayOrder": 2, "body": "<p>...</p>",
 *           "config": { ... } }
 *       ]
 *     } }
 *
 * `config` may arrive as a JSON string (if the column is stored as text) or as
 * an object; normaliseModule handles both. `sections` is sorted by
 * displayOrder here rather than trusting the order it arrives in - the theme
 * ordering work showed that unordered queries are a real risk in this codebase.
 *
 * Neither GET endpoint is deployed yet. Until they are, a transport-level
 * failure (network error, 404, 5xx) falls back to trainingFixture.js and flags
 * the result with source: 'fixture', so the UI can say so plainly. That is one
 * mechanism shared by both reads, not one per screen. A 401 is not handled
 * here - axiosInterceptor already sends the user back to login - and a
 * well-formed response that is not a module is a real error, not something to
 * paper over with the fixture.
 */

// The API is on an under-provisioned box; a request with no ceiling is how a
// loading spinner ends up spinning forever.
const REQUEST_TIMEOUT_MS = 15000;

export const PHASE_TYPES = [
  'UNDERSTAND',
  'NOTICE',
  'PRACTICE',
  'PLAN',
  'REHEARSE',
  'CHECKIN',
];

export const ENGAGEMENT_EVENTS = {
  SHOWN: 'SHOWN',
  OPENED: 'OPENED',
  COMPLETED: 'COMPLETED',
  HELPFUL: 'HELPFUL',
  NOT_HELPFUL: 'NOT_HELPFUL',
};

function getArticle(slug) {
  return axiosInterceptor.get(GET_CONTENT_ARTICLE + encodeURIComponent(slug), {
    timeout: REQUEST_TIMEOUT_MS,
  });
}

function getRecommendations(assessmentId) {
  return axiosInterceptor.get(GET_CONTENT_RECOMMENDATIONS, {
    params: { assessmentId: assessmentId },
    timeout: REQUEST_TIMEOUT_MS,
  });
}

function saveEngagement(payload) {
  return axiosInterceptor.post(SAVE_CONTENT_ENGAGEMENT, payload, {
    timeout: REQUEST_TIMEOUT_MS,
  });
}

function parseConfig(config) {
  if (config == null) return {};
  if (typeof config === 'object') return config;
  if (typeof config === 'string') {
    try {
      const parsed = JSON.parse(config);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (e) {
      // A malformed config should cost that phase its extras, not the module.
      console.warn('training: could not parse section config as JSON', e);
      return {};
    }
  }
  return {};
}

function normaliseSection(section, index) {
  const type = String(section.sectionType || section.section_type || '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_')
    .replace('CHECK_IN', 'CHECKIN');

  return {
    // sectionId is the engagement key; slug is the stable address and the key
    // this player stores per-phase answers under. Fall back to the index only
    // so a content mistake cannot collapse two phases onto one React key.
    sectionId: section.sectionId != null ? section.sectionId : null,
    slug: section.slug || 'section-' + index,
    sectionType: PHASE_TYPES.indexOf(type) >= 0 ? type : 'UNDERSTAND',
    heading: section.heading || '',
    body: section.body || '',
    displayOrder:
      typeof section.displayOrder === 'number' ? section.displayOrder : index,
    config: parseConfig(section.config),
  };
}

function normaliseModule(article, source) {
  const rawSections = Array.isArray(article.sections) ? article.sections : [];
  const sections = rawSections
    .map(normaliseSection)
    .sort((a, b) => a.displayOrder - b.displayOrder);

  return {
    articleId: article.articleId != null ? article.articleId : null,
    slug: article.slug || '',
    title: article.title || 'Training module',
    summary: article.summary || '',
    contentType: String(article.contentType || 'MODULE').toUpperCase(),
    completion: article.completion || null,
    sections,
    source,
  };
}

/**
 * Loads a training module by slug, normalised and ready to render.
 *
 * Resolves with { ...module, source: 'api' | 'fixture' }. Rejects with an Error
 * whose message is safe to show when the article exists but cannot be played.
 */
async function loadModule(slug) {
  if (!slug) {
    throw new Error('No training module was requested.');
  }

  let article = null;
  try {
    const response = await getArticle(slug);
    article = response && response.data ? response.data.data : null;
  } catch (error) {
    // Not deployed yet, offline, or down: play the fixture and say so.
    console.warn(
      'training: content API unavailable for slug "' + slug + '", using fixture content',
      error
    );
    return normaliseModule(FIXTURE_MODULE, 'fixture');
  }

  if (!article) {
    throw new Error('That training module could not be found.');
  }

  const module = normaliseModule(article, 'api');
  if (module.contentType !== 'MODULE') {
    throw new Error('That content is not a training module.');
  }
  if (module.sections.length === 0) {
    throw new Error('That training module has no phases to play yet.');
  }
  return module;
}

/* ---------------- guidance ---------------- */

/**
 * A guidance section, kept as authored.
 *
 * Deliberately not normaliseSection: that one coerces anything it does not
 * recognise to UNDERSTAND, which is right for a player that must render one of
 * six phases and wrong here, where every section is PROSE and the type is not
 * what decides the rendering.
 */
function normaliseGuidanceSection(section, index) {
  return {
    sectionId: section.sectionId != null ? section.sectionId : null,
    // The slug is the anchor an assessment result links to, so a missing one
    // still has to produce something addressable and unique on the page.
    slug: section.slug || 'section-' + index,
    sectionType: String(section.sectionType || 'PROSE').trim().toUpperCase(),
    heading: section.heading || '',
    body: section.body || '',
    displayOrder:
      typeof section.displayOrder === 'number' ? section.displayOrder : index,
  };
}

/**
 * Loads a guidance article by slug, in author order.
 *
 * Rejects with a message safe to show. Unlike loadModule there is no fixture
 * fallback: invented guidance would be worse than an honest empty state, since
 * someone could act on it.
 */
async function loadGuidance(slug) {
  if (!slug) {
    throw new Error('No guidance page was requested.');
  }

  let article = null;
  try {
    const response = await getArticle(slug);
    article = response && response.data ? response.data.data : null;
  } catch (error) {
    console.warn('guidance: could not load "' + slug + '"', error);
    throw new Error('That guidance could not be loaded. Please try again.');
  }

  if (!article) {
    throw new Error('That guidance page could not be found.');
  }

  const rawSections = Array.isArray(article.sections) ? article.sections : [];
  const sections = rawSections
    .map(normaliseGuidanceSection)
    .sort((a, b) => a.displayOrder - b.displayOrder);

  if (sections.length === 0) {
    throw new Error('That guidance page has no content yet.');
  }

  return {
    articleId: article.articleId != null ? article.articleId : null,
    slug: article.slug || slug,
    title: article.title || 'Guidance',
    summary: article.summary || '',
    contentType: String(article.contentType || 'GUIDANCE').toUpperCase(),
    sections,
  };
}

/* ---------------- recommendations ---------------- */

function positiveIntOrNull(value) {
  const number = Number(value);
  if (!isFinite(number) || number <= 0) return null;
  return Math.round(number);
}

/**
 * Turns whatever the endpoint returns into a list this UI can render.
 *
 * The contract says "ranked items", and section 5 illustrates it with a single
 * object, so accept both plus the { items: [...] } envelope a paged endpoint
 * would grow into. Rank order is whatever the server sent and is left alone -
 * ranking is the resolver's job (section 4), not the client's.
 *
 * An item with no articleSlug is dropped rather than rendered: the whole point
 * of the entry point is that it goes somewhere, and a card that links nowhere
 * is worse than no card.
 */
function normaliseRecommendations(payload) {
  let list = [];
  if (Array.isArray(payload)) {
    list = payload;
  } else if (payload && Array.isArray(payload.items)) {
    list = payload.items;
  } else if (payload && typeof payload === 'object') {
    list = [payload];
  }

  const items = [];
  list.forEach((item) => {
    if (!item || typeof item !== 'object') return;
    const articleSlug = String(item.articleSlug || '').trim();
    if (!articleSlug) return;
    items.push({
      conceptSlug: String(item.conceptSlug || '').trim(),
      articleSlug: articleSlug,
      sectionSlug: String(item.sectionSlug || '').trim(),
      title: String(item.title || '').trim() || 'Guidance',
      summary: item.summary == null ? '' : String(item.summary),
      readingMinutes: positiveIntOrNull(item.readingMinutes),
      // GUIDANCE is read at /guidance, a MODULE is played at /training. An
      // older API sent no type, and its only consumer was the training player.
      contentType: String(item.contentType || 'MODULE').toUpperCase(),
    });
  });
  return items;
}

/**
 * Loads the training recommended for one assessment result.
 *
 * Resolves with { items, source: 'api' | 'fixture' } and never rejects on
 * transport: a report that already rendered must not lose its recommendations
 * block because the content service is not live yet. An empty items list is a
 * legitimate answer - section 4 says the resolver returns nothing rather than
 * something wrong - and the UI treats it as "no training for this result".
 */
async function loadRecommendations(assessmentId) {
  if (assessmentId == null || assessmentId === '') {
    return { items: [], source: 'api' };
  }

  let payload = null;
  try {
    const response = await getRecommendations(assessmentId);
    payload = response && response.data ? response.data.data : null;
  } catch (error) {
    // A customer's report must never show sample content as if it were theirs.
    // In production a failure means no recommendations; the fixture is for
    // local development and tests only.
    if (process.env.NODE_ENV === 'production') {
      console.error('training: recommendations API unavailable for assessment ' + assessmentId, error);
      return { items: [], source: 'api' };
    }
    console.warn(
      'training: recommendations API unavailable for assessment ' +
        assessmentId +
        ', using fixture content',
      error
    );
    return {
      items: normaliseRecommendations(FIXTURE_RECOMMENDATIONS),
      source: 'fixture',
    };
  }

  return { items: normaliseRecommendations(payload), source: 'api' };
}

/**
 * Records an engagement event. Deliberately never rejects: analytics must not
 * be able to break a session someone is in the middle of. Failures are logged
 * with enough context to find them.
 */
async function recordEngagement({ articleId, sectionId, assessmentId, event }) {
  if (!articleId || !event) {
    // The fixture has no article id, so there is nothing real to record
    // against. Skip quietly rather than posting rows the API would reject.
    return false;
  }
  try {
    await saveEngagement({
      articleId: articleId,
      sectionId: sectionId != null ? sectionId : null,
      assessmentId: assessmentId != null ? assessmentId : null,
      event: event,
    });
    return true;
  } catch (error) {
    console.warn(
      'training: could not record "' + event + '" engagement for article ' + articleId,
      error
    );
    return false;
  }
}

export {
  getArticle,
  loadGuidance,
  getRecommendations,
  saveEngagement,
  loadModule,
  loadRecommendations,
  normaliseRecommendations,
  recordEngagement,
};
